import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useNotify } from '@/shared/toast/ToastProvider'
import { getErrorMessage } from '@/api/http/getErrorMessage'
import { useAvailableSurveys } from '@/features/home/api/available-surveys-queries'
import { useSpProfile } from '@/features/rewards/api'
import { useAbandonEkycSession, useCreateEkycSession } from '@/features/ekyc/api/ekyc-mutations'
import { EkycHomePrompt } from '@/features/ekyc/components/EkycHomePrompt'
import { EkycPrerequisitesDialog } from '@/features/ekyc/components/EkycPrerequisitesDialog'
import { EkycIframeDialog } from '@/features/ekyc/components/EkycIframeDialog'
import { NonQualifiedCampaignPrompt } from '@/features/home/components/NonQualifiedCampaignPrompt'
import {
  isWebSurveySignup,
  persistNonQualifiedPromptDismissed,
  readNonQualifiedPromptDismissed,
  shouldShowNonQualifiedCampaignPrompt,
} from '@/features/home/lib/nonQualifiedCampaignPrompt'
import {
  canStartEkycVerification,
  clearEkycHomeSkipped,
  isEkycUnderReview,
  persistEkycHomeSkipped,
  readEkycHomeSkipped,
} from '@/features/ekyc/lib/ekycUi'
import { useEkycPromptStore } from '@/stores/useEkycPromptStore'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  getCachedSignupSurveyCard,
  isEligibilityChecked,
  isSignupSurveySubmitted,
  shouldSuppressHomeEkycAutoPrompt,
  useSignupSurveyEligibility,
} from '@/features/signup-survey'
import { useSpKycConfiguration } from '@/features/kyc-config'

/**
 * Hosts eKYC modals at shell level so Home auto-prompt and nav-chip reopen both work.
 *
 * Skip / Close only suppress the Home auto-prompt for this browser session.
 * They never permanently remove eKYC — Unverified pill always reopens the flow.
 *
 * BFP-1025 / BFP-1031: Home auto-prompt must not flash while signup-survey eligibility
 * or Home surveys are still loading. While a signup survey is owed, Verify Your Account
 * must not show on Home at all (auto or forced) — survey first, then eKYC.
 *
 * BFP-1080 / BFP-1127: Non-Independent signups get the campaign ineligibility popup
 * (Got It only — no KYC). Effective KYC disabled hides home verify auto-prompt.
 */
export function EkycPromptHost() {
  const location = useLocation()
  const { showToast } = useNotify()
  const profileQuery = useSpProfile()
  const createEkyc = useCreateEkycSession()
  const abandonEkyc = useAbandonEkycSession()
  const openRequestId = useEkycPromptStore((s) => s.openRequestId)
  const openIntent = useEkycPromptStore((s) => s.openIntent)
  const handledOpenRequestId = useRef(0)

  const [ekycSkipped, setEkycSkipped] = useState(() => readEkycHomeSkipped())
  const [forcedOpen, setForcedOpen] = useState(false)
  const [iframeSrc, setIframeSrc] = useState<string | null>(null)
  const [iframeOpen, setIframeOpen] = useState(false)
  const [prerequisitesOpen, setPrerequisitesOpen] = useState(false)
  const [nonQualDismissed, setNonQualDismissed] = useState(false)

  const userId = useSessionStore((s) => s.user?.id)
  // Subscribe so Home re-evaluates suppress after eligibility settles (localStorage alone is not reactive).
  const signupEligibilityQuery = useSignupSurveyEligibility()
  const { data: effectiveKycMode } = useSpKycConfiguration(Boolean(userId))
  const kycSurfacesEnabled = effectiveKycMode === 'enabled'
  // Shared with Home — used only to avoid auto-prompt while the survey list is still first-loading.
  const availableSurveysQuery = useAvailableSurveys()

  const profile = profileQuery.data
  const canStart = canStartEkycVerification(profile)
  const underReview = isEkycUnderReview(profile?.ekycStatus)
  const isHome = location.pathname === '/'
  const isVerified = profile?.ekycStatus === 'verified'
  const nonQualifiedDue = shouldShowNonQualifiedCampaignPrompt(profile, userId)

  useEffect(() => {
    if (!userId) {
      setNonQualDismissed(false)
      return
    }
    setNonQualDismissed(readNonQualifiedPromptDismissed(userId))
  }, [userId])

  useEffect(() => {
    if (openRequestId === 0 || openRequestId === handledOpenRequestId.current) return
    if (!kycSurfacesEnabled) return
    handledOpenRequestId.current = openRequestId
    // under_review / verified: never start (or re-open) verification UI.
    if (underReview || isVerified) {
      setForcedOpen(false)
      setPrerequisitesOpen(false)
      return
    }
    clearEkycHomeSkipped()
    setEkycSkipped(false)
    setIframeOpen(false)
    if (openIntent === 'prerequisites') {
      // Prerequisites start a session — only when eligible.
      if (!canStart) {
        setForcedOpen(false)
        setPrerequisitesOpen(false)
        return
      }
      setForcedOpen(false)
      setPrerequisitesOpen(true)
      return
    }
    // Chip / Rewards nag — show Verify prompt (incl. attempt-used copy when !canStart).
    setPrerequisitesOpen(false)
    setForcedOpen(true)
  }, [openRequestId, openIntent, underReview, isVerified, canStart, kycSurfacesEnabled])

  // Login / webhook: if status becomes under_review while a modal is open, dismiss it.
  useEffect(() => {
    if (!underReview) return
    setForcedOpen(false)
    setPrerequisitesOpen(false)
    setIframeOpen(false)
  }, [underReview])

  const signupSubmitted = Boolean(userId && isSignupSurveySubmitted(userId))
  const signupCardPresent =
    Boolean(signupEligibilityQuery.data) ||
    Boolean(userId && getCachedSignupSurveyCard(userId))

  // Signup still owed / eligibility not settled → never show Home Verify modal (BFP-1031).
  const signupSurveyBlocksHomeEkyc =
    userId != null &&
    !signupSubmitted &&
    (signupCardPresent ||
      !isEligibilityChecked(userId) ||
      !signupEligibilityQuery.isFetched ||
      signupEligibilityQuery.isPending ||
      signupEligibilityQuery.isFetching)

  // Available surveys still on first load — same split-second race users hit on fresh login.
  const homeSurveysUnresolved =
    isHome && Boolean(userId) && !availableSurveysQuery.isFetched

  const suppressHomeEkyc =
    !kycSurfacesEnabled ||
    signupSurveyBlocksHomeEkyc ||
    homeSurveysUnresolved ||
    shouldSuppressHomeEkycAutoPrompt(userId ?? '') ||
    nonQualifiedDue ||
    nonQualDismissed ||
    isWebSurveySignup(profile)

  const nonQualOpen =
    isHome &&
    nonQualifiedDue &&
    !nonQualDismissed &&
    !isVerified &&
    !underReview &&
    !iframeOpen &&
    !prerequisitesOpen

  // If signup card arrives after eKYC already auto-opened, force the modal closed.
  useEffect(() => {
    if (!signupSurveyBlocksHomeEkyc) return
    setForcedOpen(false)
  }, [signupSurveyBlocksHomeEkyc])

  const autoOpen = isHome && Boolean(userId) && canStart && !ekycSkipped && !suppressHomeEkyc
  const promptOpen =
    !isVerified &&
    !underReview &&
    (forcedOpen || autoOpen) &&
    !iframeOpen &&
    !prerequisitesOpen &&
    // Hard gate: signup survey on Home always wins over Verify Your Account.
    !signupSurveyBlocksHomeEkyc &&
    !nonQualOpen

  const handleVerify = async () => {
    try {
      const session = await createEkyc.mutateAsync()
      setPrerequisitesOpen(false)
      setForcedOpen(false)
      setIframeSrc(session.iframeSrc)
      setIframeOpen(true)
    } catch (err) {
      showToast(getErrorMessage(err), 'error')
    }
  }

  /** Temporary dismiss only — Home auto-prompt stays off this session; pill can reopen anytime. */
  const handleDismiss = () => {
    setPrerequisitesOpen(false)
    setForcedOpen(false)
    persistEkycHomeSkipped()
    setEkycSkipped(true)
  }

  const handleNonQualGotIt = () => {
    if (userId) persistNonQualifiedPromptDismissed(userId)
    setNonQualDismissed(true)
  }

  const handleCloseIframe = async () => {
    setIframeOpen(false)
    setIframeSrc(null)
    setForcedOpen(false)
    setPrerequisitesOpen(false)
    try {
      const updated = await abandonEkyc.mutateAsync()
      // Free close/abandon — keep Verify Me available this session.
      if (updated.ekycAttemptUsed !== true && updated.ekycStatus === 'not_started') {
        clearEkycHomeSkipped()
        setEkycSkipped(false)
      }
    } catch {
      /* webhook may already have finalized */
    }
    await profileQuery.refetch().catch(() => undefined)
  }

  return (
    <>
      <NonQualifiedCampaignPrompt open={nonQualOpen} onGotIt={handleNonQualGotIt} />

      <EkycHomePrompt
        open={promptOpen}
        canStart={canStart}
        onVerify={() => {
          if (!canStart) return
          setPrerequisitesOpen(true)
        }}
        onSkip={handleDismiss}
        isStarting={false}
      />

      <EkycPrerequisitesDialog
        open={prerequisitesOpen && !iframeOpen && canStart && !underReview}
        onConfirm={() => {
          handleVerify().catch(() => undefined)
        }}
        onClose={handleDismiss}
        isStarting={createEkyc.isPending}
      />

      <EkycIframeDialog
        open={iframeOpen}
        iframeSrc={iframeSrc}
        onClose={() => {
          handleCloseIframe().catch(() => undefined)
        }}
      />
    </>
  )
}
